// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import type { FeatureSetting, FeatureWireValue } from '../../api/pharmacyFeatures'
import { formatUsDateLocal } from '../../api/format'
import { friendlyError } from '../../api/errors'
import { useCareRxFeatures, useSetCareRxFeature } from '../../hooks/queries/pharmacyFeatures'
import AppButton from '../ui/AppButton'
import DateInput from '../ui/DateInput'
import EmptyState from '../ui/EmptyState'
import { AppTextField, SelectField, ToggleSwitch } from '../ui/FormFields'
import LoadState from '../ui/LoadState'
import Section from '../ui/Section'
import StatusChip from '../ui/StatusChip'
import { useToast } from '../ui/Toast'
import ConfirmDialog from '../ui/ConfirmDialog'
import { SIGNING_MODE_KEY } from '../../api/inappEsign'

/**
 * D-CRX-87 §1 — SaaS admin → pharmacy → CareRx tab: the per-pharmacy CareRx settings (control plane, mig 446).
 * Every setting shows Default or Overridden; Default is today's behaviour, and "Reset to default" deletes the
 * saved row. The list and the input for each setting come from the server's catalog, so a new setting needs
 * no change here.
 */
export default function CareRxFeaturesPanel({ pharmacyId }: { pharmacyId: string }) {
  const q = useCareRxFeatures(pharmacyId)
  const cache = q.data?.cache_seconds ?? 0
  // D-CRX-87 §4d: whether DocuSign is set up for this pharmacy — the Signing row warns when it is chosen without it.
  const docusignReady = q.data?.docusign_ready !== false
  return (
    <Section title="CareRx settings"
      subtitle="Per-pharmacy CareRx behaviour. A setting at Default keeps today's behaviour for this pharmacy.">
      <LoadState query={{ ...q, data: q.data?.features }} what="the CareRx settings"
        empty={<EmptyState title="No CareRx settings yet" hint="Settings appear here as CareRx features that can be switched per pharmacy are released." />}>
        {features => (
          <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
            {features.map(f => <FeatureRow key={f.key} f={f} pharmacyId={pharmacyId} docusignReady={docusignReady} />)}
          </Stack>
        )}
      </LoadState>
      {q.data && (
        <Box sx={{ mt: 1, fontSize: 11.5, color: 'text.secondary' }}>
          {cache > 0 ? `A change applies within ${cache} seconds.` : 'A change applies to the pharmacy’s next request.'}
        </Box>
      )}
    </Section>
  )
}

/**
 * D-CRX-87 §4d (owner 2026-10-21, latest) — the warnings under the Signing row. The server enforces every mode;
 * these tell the SaaS admin what the chosen mode means for this pharmacy before anyone hits a refusal.
 */
export const SIGNING_ROW_WARNINGS = {
  docusignNotSetUp: 'DocuSign is not set up for this pharmacy — only in-app signing will work.',
  inAppOnly: 'In app only: patient consent and Plan of Care cannot be sent; the enrollment form and the Plan of Treatment are signed by a prescriber with an OwlRx login on their provider portal.',
} as const

export function signingRowWarnings(mode: FeatureWireValue, docusignReady: boolean): string[] {
  const out: string[] = []
  if ((mode === 'docusign' || mode === 'both') && !docusignReady) out.push(SIGNING_ROW_WARNINGS.docusignNotSetUp)
  if (mode === 'in_app') out.push(SIGNING_ROW_WARNINGS.inAppOnly)
  return out
}

function FeatureRow({ f, pharmacyId, docusignReady }: { f: FeatureSetting; pharmacyId: string; docusignReady: boolean }) {
  const toast = useToast()
  const set = useSetCareRxFeature(pharmacyId)
  /** Returns the save's promise, so a date field can put the saved value back when it fails. */
  const save = (value: FeatureWireValue) => set.mutateAsync({ key: f.key, value }, {
    onSuccess: s => toast.show(s.overridden ? `${f.label} saved.` : `${f.label} is back to its default.`, 'success'),
    onError: e => toast.show(friendlyError(e), 'error'),
  })
  // Fire-and-report for controls that do not revert themselves; the failure is already shown as a toast.
  const send = (value: FeatureWireValue) => { save(value).catch(() => undefined) }
  // D-CRX-87 §4d: choosing "In app only" asks first — nothing is saved until the admin confirms.
  const isSigning = f.key === SIGNING_MODE_KEY
  const [confirmInApp, setConfirmInApp] = useState(false)
  const choose = (value: FeatureWireValue) => {
    if (isSigning && value === 'in_app' && f.value !== 'in_app') { setConfirmInApp(true); return }
    send(value)
  }
  const warnings = isSigning ? signingRowWarnings(f.value, docusignReady) : []

  return (
    <Box sx={{ py: 1.25 }} data-feature={f.key}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box component="span" sx={{ fontSize: 13, fontWeight: 600 }}>{f.label}</Box>
            <StatusChip status={f.overridden ? 'overridden' : 'default'} label={f.overridden ? 'Overridden' : 'Default'}
              tone={f.overridden ? 'blue' : 'grey'} />
          </Stack>
          <Box sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>{f.description}</Box>
          {f.pharmacy_editable && (
            // D-CRX-87b A6: the pharmacy may change this one itself too (CareRx admin).
            <Box sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }} data-testid="feature-pharmacy-editable">
              The pharmacy can also change this in CareRx admin.
            </Box>
          )}
          {warnings.map(w => (
            <Box key={w} data-testid="signing-warning" sx={{ fontSize: 12, color: 'warning.dark', fontWeight: 600, mt: 0.5 }}>{w}</Box>
          ))}
          {f.overridden && (
            <Box sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }} data-testid="feature-set-by">
              Set by {f.updated_by_name ?? 'a SaaS admin'} on {formatUsDateLocal(f.updated_at)}
            </Box>
          )}
        </Box>
        <FeatureControl f={f} disabled={set.isPending} save={save} send={choose} />
        <AppButton size="small" variant="outlined" disabled={!f.overridden || set.isPending} onClick={() => send(null)}>
          Reset to default
        </AppButton>
      </Stack>
      {isSigning && (
        <ConfirmDialog open={confirmInApp} title="Switch Signing to In app only?"
          body={<Stack spacing={1} sx={{ fontSize: 13.5 }}>
            <Box>Pharmacy staff will sign the enrollment form inside OwlRx; nothing is emailed.</Box>
            <Box sx={{ fontWeight: 600 }}>{SIGNING_ROW_WARNINGS.inAppOnly}</Box>
            <Box>Existing DocuSign envelopes keep working (status, void, download, audit). Needs tenant migration 447.</Box>
          </Stack>}
          confirmLabel="Switch to In app only" busy={set.isPending}
          onCancel={() => setConfirmInApp(false)}
          onConfirm={() => { setConfirmInApp(false); send('in_app') }} />
      )}
    </Box>
  )
}

function FeatureControl({ f, disabled, save, send }: {
  f: FeatureSetting; disabled: boolean
  save: (v: FeatureWireValue) => Promise<unknown>
  send: (v: FeatureWireValue) => void
}) {
  switch (f.type) {
    case 'date':
      return <DateInput label={f.label} value={typeof f.value === 'string' ? f.value : null} disabled={disabled}
        onChange={iso => save(iso)} style={{ width: 150 }} />
    case 'bool':
      return <ToggleSwitch label={f.value === true ? 'On' : 'Off'} checked={f.value === true} disabled={disabled} onChange={v => send(v)} />
    case 'int':
      return <IntField f={f} disabled={disabled} send={send} />
    default:
      return <SelectField size="small" label={f.label} sx={{ minWidth: 180 }} disabled={disabled}
        value={typeof f.value === 'string' ? f.value : ''} onChange={v => send(v)}
        options={f.options ?? []} />
  }
}

/** A whole number, saved on Enter or on leaving the field. Empty = back to the default. Out of range = not sent. */
export function intProblem(text: string, min: number | null, max: number | null): string | null {
  const t = text.trim()
  if (t === '') return null
  if (!/^-?\d+$/.test(t)) return 'Whole number only'
  const n = Number(t)
  if (min != null && n < min) return `At least ${min}`
  if (max != null && n > max) return `At most ${max}`
  return null
}

function IntField({ f, disabled, send }: { f: FeatureSetting; disabled: boolean; send: (v: FeatureWireValue) => void }) {
  const saved = typeof f.value === 'number' ? String(f.value) : ''
  const [text, setText] = useState(saved)
  useEffect(() => setText(saved), [saved])
  const problem = intProblem(text, f.min, f.max)
  const commit = () => {
    if (problem || text.trim() === saved) return
    send(text.trim() === '' ? null : Number(text.trim()))
  }
  return (
    <AppTextField size="small" label={f.label} value={text} disabled={disabled} sx={{ width: 150 }}
      inputProps={{ inputMode: 'numeric' }} error={!!problem} helperText={problem ?? ' '}
      onChange={e => setText(e.target.value)} onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit() } }} />
  )
}
